import {
  Button,
  ModalActions,
  ModalHeader,
  Stack,
  colors,
  Typography,
  CheckboxContainer,
  getRecipeColor,
  ControlledCheckbox,
  TitleWithHelp,
  HelpLink,
} from '@ui-components/neo.tax';
import { ReactComponent as AlarmBellRing } from '@ui-components/neo.tax/dist/icons/alarm-bell-ring.svg';
import { useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { useUpdateRdProject, RdProject } from '@neo.tax/rd/hooks';
import { HELP_PROJECT_DISALLOWANCES_URL, evictQres, ProjectEligibilityModal, HIGHLIGHT_VARIANT } from '@neo.tax/shared';

import { useRdProjectDisallowanceForm, useRdProjectDisallowanceOptions } from './hooks';
import { ButtonSpanTwoColumns, ProjectDisallowancesSubHeader } from './rd-project-disallowances-modal.styled';
import type { RdProjectDisallowancesForm } from './rd-project-disallowances-modal.types';
import { RD_PROJECT_DISALLOWANCES, reduceRdProjectDisallowances } from './utils';

type RdProjectDisallowancesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  rdProject: RdProject;
};

export const RdProjectDisallowancesModal = ({
  isOpen,
  onClose,
  rdProject,
}: RdProjectDisallowancesModalProps): React.ReactElement => {
  const { disallowanceOptions, noneOfTheAboveOption } = useRdProjectDisallowanceOptions();
  const [updateRdProject] = useUpdateRdProject({ rdProject, includeOptimisticResponse: true });
  const { control, formState, handleSubmit, reset, watch } = useRdProjectDisallowanceForm({
    rdProject,
  });

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleResetAndSubmit = (): void => {
    const rdProjectDisallowances = reduceRdProjectDisallowances((): boolean => false);
    onSubmit({
      ...rdProjectDisallowances,
      noneOfTheAbove: false,
    });
  };

  const onSubmit: SubmitHandler<RdProjectDisallowancesForm> = async (data) => {
    const selectedDisallowances = RD_PROJECT_DISALLOWANCES.filter((disallowance) => data[disallowance]);
    // disallowances: null -> nothing selected
    // disallowances: [] -> "none of the above" selected
    // disallowances: [disallowance] -> one or more disallowances selected
    const disallowances = data.noneOfTheAbove ? [] : selectedDisallowances.length ? selectedDisallowances : null;

    await updateRdProject({ disallowances }, { update: evictQres });
    onClose();
  };

  const allValues = watch();
  const noneOfTheAbove = watch('noneOfTheAbove');
  const nothingSelected = Object.values(allValues).every((value) => !value);

  useEffect(() => {
    if (noneOfTheAbove) {
      const disallowances = reduceRdProjectDisallowances((): boolean => false);
      reset(
        {
          ...disallowances,
          noneOfTheAbove: true,
        },
        {
          keepDefaultValues: true,
        },
      );
    }
  }, [noneOfTheAbove, reset]);

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <ProjectEligibilityModal isOpen={isOpen}>
      <Stack as="form" direction="column" gap={8} onSubmit={handleSubmit(onSubmit)} paddingX={4}>
        <ModalHeader
          icon={<AlarmBellRing width={24} color={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').text} />}
          iconBgColor={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').bg}
          title={
            <TitleWithHelp
              title={
                <>
                  Project{' '}
                  <Typography
                    fontSize="inherit"
                    fontWeight="inherit"
                    color={getRecipeColor(HIGHLIGHT_VARIANT, 'whiteBg').text}
                  >
                    {rdProject.name}
                  </Typography>{' '}
                  Disallowances
                </>
              }
              href={HELP_PROJECT_DISALLOWANCES_URL}
            />
          }
          copy="Certain activities are explicitly disqualified from the R&D credit"
        />
        <Stack direction="column">
          <ProjectDisallowancesSubHeader fontSize="sm" fontWeight="medium" color={colors.gray[400]}>
            Did the project include any of the below disqualified activities?
            <HelpLink href={HELP_PROJECT_DISALLOWANCES_URL} />
          </ProjectDisallowancesSubHeader>
          <CheckboxContainer>
            {disallowanceOptions.map(({ id, label, value }) => (
              <ControlledCheckbox
                key={id}
                control={control}
                name={value}
                id={id}
                label={label}
                disabled={noneOfTheAbove}
              />
            ))}
          </CheckboxContainer>
        </Stack>
        <hr />
        <ControlledCheckbox
          control={control}
          name={noneOfTheAboveOption.value}
          id={noneOfTheAboveOption.id}
          label={noneOfTheAboveOption.label}
        />
        <ModalActions hasTopBorder={false}>
          <ButtonSpanTwoColumns
            variant="primary"
            rounded="regular"
            size="base"
            type="submit"
            disabled={!formState.isDirty || nothingSelected}
          >
            Save
          </ButtonSpanTwoColumns>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="action"
            rounded="regular"
            size="base"
            onClick={handleResetAndSubmit}
            disabled={!formState.isDirty && nothingSelected}
          >
            Reset & Close
          </Button>
        </ModalActions>
      </Stack>
    </ProjectEligibilityModal>
  );
};
